import { Component, inject, signal } from '@angular/core';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';
import { MessageService } from 'primeng/api';
import { ApiService } from '../../api.service';
import { TAGFinanceTransaction } from '../../../models/tagfinances';
import { FormGroup, FormControl, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { TAGUser } from '../../../models/taguser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { SkeletonModule } from 'primeng/skeleton';
import { DatePickerModule } from 'primeng/datepicker';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FluidModule } from 'primeng/fluid';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CdkDrag, DragDropModule } from '@angular/cdk/drag-drop';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { ButtonModule } from 'primeng/button';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Router } from '@angular/router';
import { InputNumberModule } from 'primeng/inputnumber';
import {
  newProgressDialogStep,
  openProgressDialog,
  ProgressDialogStep,
  ProgressDialogStepState,
} from '../../components/progressDialog/progressDialog';
import { DialogService } from 'primeng/dynamicdialog';
pdfMake.addVirtualFileSystem(pdfFonts);

export interface InvoiceData {
  reason?: string;
  date?: Date;
  user?: string;
  eventid?: string;
  availableItems?: TAGFinanceTransaction[];
}

export interface AddressTemplate {
  name: string;
  address: string[];
}

@Component({
  selector: 'app-internal-invoice-editor',
  imports: [
    InternalToolbar,
    ReactiveIconTextButton,
    TranslatePipe,
    FloatLabelModule,
    InputTextModule,
    InputNumberModule,
    AutoCompleteModule,
    SkeletonModule,
    ReactiveFormsModule,
    DatePickerModule,
    FluidModule,
    SelectModule,
    ToggleSwitchModule,
    CdkDrag,
    DragDropModule,
    MatIcon,
    ButtonModule,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-invoice-editor.html',
  styleUrl: './internal-invoice-editor.scss',
})
export class InternalInvoiceEditor {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  //private route = inject(ActivatedRoute);
  public api = inject(ApiService);
  private sanitizer = inject(DomSanitizer);

  addressTemplates: AddressTemplate[] = [
    {
      name: 'SAK',
      address: ['Fanny-Leicht Gymnasium', 'SAK', 'Fanny-Leicht-Straße 13', '70563 Stuttgart'],
    },
  ];

  routeState: InvoiceData = {
    availableItems: [
      new TAGFinanceTransaction({ _id: '1', reason: 'Test Item 1', amount: 101 }),
      new TAGFinanceTransaction({ _id: '2', reason: 'Test Item 2', amount: 102 }),
      new TAGFinanceTransaction({ _id: '3', reason: 'Test Item 3', amount: 103 }),
    ],
  };

  availableTransactions: TAGFinanceTransaction[] = [];
  get unusedAvailTransactions() {
    return this.availableTransactions.filter(
      (t) => !this.invoiceItems.find((i) => i.value.transactionID == t._id),
    );
  }

  invoiceForm: FormGroup = new FormGroup({
    reason: new FormControl('', [Validators.required]),
    date: new FormControl<Date>(new Date(), [Validators.required]),
    user: new FormControl<TAGUser>(undefined, [Validators.required]),
    addressLine1: new FormControl('', [Validators.required]),
    addressLine2: new FormControl(''),
    addressLine3: new FormControl(''),
    addressLine4: new FormControl(''),
    showQR: new FormControl(true),
    invoiceItems: new FormArray([]),
  });

  users = signal<TAGUser[]>([]);
  loadingUsers = signal(true);
  filteredUsers = signal<TAGUser[]>([]);

  get invoiceItems() {
    return (this.invoiceForm.get('invoiceItems') as FormArray).controls;
  }
  get total(): number {
    return this.invoiceItems.reduce((prev, curr: FormGroup) => {
      return prev + curr.controls['quantity'].value * curr.controls['unitPrice'].value;
    }, 0);
  }

  pdfData = signal<SafeResourceUrl>('');
  lastChangedTimer: any = null;

  _searchUser(event: AutoCompleteCompleteEvent) {
    this.filteredUsers.set(
      this.users().filter(
        (user) =>
          user.username.toLowerCase().includes(event.query.toLowerCase()) ||
          user.firstname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.lastname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.fullname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.email.toLowerCase().includes(event.query.toLowerCase()),
      ),
    );

    console.log(event.query, this.filteredUsers());
  }

  constructor() {
    this.generatePDF.bind(this);

    console.log(this.router.currentNavigation()?.extras.state);
    if (this.router.currentNavigation()?.extras.state) {
      this.routeState = this.router.currentNavigation().extras.state as InvoiceData;
    }
  }

  ngOnInit(): void {
    this.pdfData.set(this.sanitizer.bypassSecurityTrustResourceUrl('') as string);

    if (this.routeState) {
      if (this.routeState.reason) {
        this.invoiceForm.patchValue({ reason: this.routeState.reason });
      }

      if (this.routeState.date) {
        this.invoiceForm.patchValue({ date: this.routeState.date });
      }

      if (this.routeState.user) {
        this.invoiceForm.patchValue({ user: this.routeState.user });
      }

      if (this.routeState.availableItems) {
        this.availableTransactions = this.routeState.availableItems.map((t) => {
          if (typeof t.amount == 'string') {
            t.amount = parseFloat(t.amount);
          }
          return t;
        });
      }
    }

    this.api.waitForAPI().then(() => {
      this.api
        .getUsers()
        .then((users: TAGUser[]) => {
          this.users.set(users);
          this.loadingUsers.set(false);

          let foundUser = users.find((u) => u.username === this.routeState.user);
          if (foundUser) this.invoiceForm.patchValue({ user: foundUser });
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate("errors.couldNotLoadUsers")(),
            detail: reason.message,
          });
        });
    });

    this.invoiceForm.valueChanges.subscribe((value) => {
      if (this.lastChangedTimer) {
        clearTimeout(this.lastChangedTimer);
      }

      this.lastChangedTimer = setTimeout(() => {
        this.generatePDF();
      }, 500);
    });
    this.generatePDF();
  }

  selectAddressTemplate(template: AddressTemplate) {
    this.invoiceForm.patchValue({
      addressLine1: template.address[0],
      addressLine2: template.address[1],
      addressLine3: template.address[2],
      addressLine4: template.address[3],
    });
  }

  addInvoiceItem() {
    (this.invoiceForm.get('invoiceItems') as FormArray).push(
      new FormGroup({
        transactionID: new FormControl(),
        description: new FormControl('', [Validators.required]),
        quantity: new FormControl(1, [Validators.required]),
        unitPrice: new FormControl(0, [Validators.required]),
        additionalInfo: new FormControl(''),
      }),
    );
  }

  removeInvoiceItem(index: number) {
    (this.invoiceForm.get('invoiceItems') as FormArray).removeAt(index);
  }

  addTransactionToInvoice(transaction: TAGFinanceTransaction, index: number) {
    console.log(transaction);
    let item = new FormGroup({
      transactionID: new FormControl(transaction._id),
      description: new FormControl({ value: transaction.reason, disabled: true }),
      quantity: new FormControl({ value: 1, disabled: true }),
      unitPrice: new FormControl({ value: transaction.amount.toString(), disabled: true }),
      additionalInfo: new FormControl(''),
    });

    (this.invoiceForm.get('invoiceItems') as FormArray).insert(index, item);
  }

  useItem(data) {
    console.log(data);
    if (data.previousContainer === data.container) {
      let item = this.invoiceItems[data.previousIndex];
      this.removeInvoiceItem(data.previousIndex);
      this.invoiceItems.splice(data.currentIndex, 0, item);
    } else {
      this.addTransactionToInvoice(data.item.data, data.currentIndex);
    }
  }

  unuseItem(data) {
    console.log(data);
    if (data.previousContainer === data.container) {
      let item = this.availableTransactions[data.previousIndex];
      this.availableTransactions.splice(data.previousIndex, 1);
      this.availableTransactions.splice(data.currentIndex, 0, item);
    } else {
      this.removeInvoiceItem(data.previousIndex);
    }
  }

  unusePredicate(item: CdkDrag<any>) {
    console.log(item);
    return item.data.transactionID != null;
  }

  saveInvoice() {
    if (!this.invoiceForm.valid) {
      console.log("Form is invalid");
      return;
    }

    new Promise<string>(async (resolve, reject) => {
      let transactionStep = newProgressDialogStep(
        new ProgressDialogStep(
          ProgressDialogStepState.WAITING,
          this.translate.translate('internal.invoiceForm.creatingTransactionProgress')(),
        ),
      );

      let uploadStep = newProgressDialogStep(
        new ProgressDialogStep(
          ProgressDialogStepState.WAITING,
          this.translate.translate('internal.invoiceForm.uploadingInvoiceProgress')(),
        )
          .setShowProgress(true)
          .setProgress(0),
      );

      let updateStep = newProgressDialogStep(
        new ProgressDialogStep(
          ProgressDialogStepState.WAITING,
          this.translate.translate('internal.invoiceForm.updatingTransactionProgress')(),
        ),
      );

      const progressDialog = openProgressDialog(this.dialogService, [
        transactionStep,
        uploadStep,
        updateStep,
      ]);

      progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
        transactionStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

        let transaction = new TAGFinanceTransaction({});
        transaction.reason = this.invoiceForm.get('reason').value;
        transaction.amount = this.total;
        transaction.date = this.invoiceForm.get('date').value;
        transaction.event = this.routeState.eventid;
        transaction.isIncome = true;
        transaction.pending = true;

        this.api
          .getNextInvoiceNumber()
          .then((invoiceNumber: number) => {
            this.api
              .newFinanceTransaction(transaction)
              .then((newTransaction: TAGFinanceTransaction) => {
                transactionStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
                uploadStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

                this.generatePDF(false, invoiceNumber, newTransaction._id)
                  .then((pdfDataUrl) => {
                    this.api
                      .uploadFinanceTransactionAttachment(
                        newTransaction._id,
                        'overview.pdf',
                        pdfDataUrl,
                        (progress) => {
                          uploadStep.update((s) => s.setProgress(progress));
                        },
                      )
                      .then((result) => {
                        uploadStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
                        updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

                        newTransaction.invoiceNumber = invoiceNumber;
                        newTransaction.invoiceFileName = 'overview.pdf';

                        this.api
                          .updateFinanceTransaction(newTransaction)
                          .then(async (result) => {
                            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
                            await pDialog.finishDialog(1000);
                            setTimeout(() => {
                              this.router.navigate(['/internal/finances']);
                            }, 1000);
                          })
                          .catch((reason) => {
                            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
                            updateStep.update((s) => s.setSubtitle(reason.message));
                            pDialog.finishDialog(-1);
                            console.log(reason);
                            return;
                          });
                      })
                      .catch((reason) => {
                        uploadStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
                        uploadStep.update((s) => s.setSubtitle(reason.message));
                        pDialog.finishDialog(-1);
                        console.log(reason);
                        return;
                      });
                  })
                  .catch((reason) => {
                    uploadStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
                    uploadStep.update((s) => s.setSubtitle(reason.message));
                    pDialog.finishDialog(-1);
                    console.log(reason);
                    return;
                  });
              })
              .catch((reason) => {
                transactionStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
                transactionStep.update((s) => s.setSubtitle(reason.message));
                pDialog.finishDialog(-1);
                console.log(reason);
                return;
              });
          })
          .catch((reason) => {
            transactionStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            transactionStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          });
      });
    });
  }

  generatePDF(
    preview: boolean = true,
    invoiceNumber: number = 0,
    transactionID: string = '00000000000000000000000000000000',
  ) {
    return new Promise<string>((resolve, reject) => {
      pdfMake.addFonts({
        Arial: {
          normal: location.origin + '/fonts/arial.ttf',
          bold: location.origin + '/fonts/arialbd.ttf',
          bolditalics: location.origin + '/fonts/arialbi.ttf',
          italics: location.origin + '/fonts/ariali.ttf',
        },
        Roboto: {
          normal: 'Roboto-Regular.ttf',
          bold: 'Roboto-Medium.ttf',
        },
        Icon: {
          normal: location.origin + '/fonts/material-symbols-outlined.ttf',
        },
      });

      let pdf = pdfMake.createPdf(
        this.generatePDFContent(preview, invoiceNumber, transactionID),
        {},
      );
      pdf.getDataUrl().then((dataUrl: string) => {
        this.pdfData.set(this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl) as string);
        resolve(dataUrl);
      });
    });
  }

  generatePDFContent(
    preview: boolean = true,
    invoiceNumber: number = 0,
    transactionID: string = '00000000000000000000000000000000',
  ) {
    console.log(this.invoiceItems);

    let logoURL = location.origin + '/images/logo.png';

    let qrObject = {};
    if (this.invoiceForm.get('showQR').value) {
      qrObject = {
        stack: [
          { qr: 'https://technikflg.com/document/' + transactionID, fit: 100 },
          {
            stack: [{ text: 'Online Version', alignment: 'center' }],
          },
        ],
      };
    }

    let addressText = this.invoiceForm.get('addressLine1').value + '\n';
    addressText += this.invoiceForm.get('addressLine2').value + '\n';
    addressText += this.invoiceForm.get('addressLine3').value + '\n';
    addressText += this.invoiceForm.get('addressLine4').value;

    let invoiceItemTableArr: any[] = this.invoiceItems.map((item: FormGroup, i) => {
      let description: any[] = [{ text: item.controls['description'].value, bold: true }];
      if (item.controls['additionalInfo'].value) {
        description.push({ text: item.controls['additionalInfo'].value });
      }

      return [
        { text: i + 1, style: 'tableItemsGrey' },
        description,
        { text: item.controls['quantity'].value, style: ['tableItemsGrey', 'tableItemsRight'] },
        {
          text: (parseFloat(item.controls['unitPrice'].value) ?? 0).toFixed(2) + ' €',
          style: ['tableItemsGrey', 'tableItemsRight'],
        },
        {
          text:
            (item.controls['quantity'].value * item.controls['unitPrice'].value).toFixed(2) + ' €',
          style: ['tableItemsRight'],
        },
      ];
    });

    invoiceItemTableArr.push([
      { text: '', style: 'tableItemsGrey' },
      { text: 'Gesamt', bold: true },
      { text: '', style: 'tableItemsGrey' },
      { text: '', style: 'tableItemsGrey' },
      { text: this.total.toFixed(2) + ' €', bold: true, style: ['tableItemsRight'] },
    ]);

    /* var canvas = document.getElementById("barcode") as HTMLCanvasElement;
    var context = canvas.getContext("2d");
    //PDF417.draw("testcode", canvas, 3, 8)
    JsBarcode(canvas, '123456', {
      format: "CODE128",
      lineColor: "#000000",
      width: 1,
      height: 10,
      displayValue: true,
      fontSize: 4,
      margin: 0,
    });
    canvas.getContext("2d").rotate(270 * Math.PI / 180);
    const barcode = canvas.toDataURL('image/png') */

    console.log(invoiceItemTableArr);

    let docDefinition: TDocumentDefinitions = {
      info: {
        title: 'Kosten Übersicht bezüglich ' + this.invoiceForm.value.reason,
      },
      pageMargins: [70, 57, 43, 57 * 2 + 20],
      content: [
        {
          layout: 'noBorders',
          table: {
            widths: ['*', 100],
            body: [[{ image: 'logo', width: 132 }, qrObject]],
          },
        },
        /* { image: "barcode", width: 132, absolutePosition: { x: 70, y: 57 + 20 }, style: {  } }, */

        //marginTop: this.invoiceForm.get("showQR").value? 30 : 82 + 30
        {
          text: 'Technik AG, Fanny-Leicht-Gymnasium,\nFanny-Leicht-Str. 13, 70563 Stuttgart',
          style: 'addressSender',
          absolutePosition: { x: 70, y: 128 },
        },
        { text: addressText, style: 'addressText', absolutePosition: { x: 70, y: 128 + 28 } },

        {
          text: [
            'Kosten Übersicht bezüglich ',
            { text: this.invoiceForm.value.reason, decoration: 'underline' },
          ],
          style: 'header',
          marginTop: this.invoiceForm.get('showQR').value ? 55 : 150,
        },

        {
          marginTop: 8,
          table: {
            widths: [141, '*'],
            body: [
              [
                'Erstellungsdatum',
                (this.invoiceForm.value.date as Date).toLocaleDateString(['de'], {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }),
              ],
              ['Dokumentennummer', invoiceNumber],
              ['Sachbearbeiter', this.invoiceForm.value.user?.fullname ?? 'N/A'],
            ],
          },
          layout: 'noBorders',
        },

        {
          marginTop: 38,
          style: 'tableItems',
          table: {
            widths: ['auto', '*', 40, 50, 50],
            heights: 16,
            headerRows: 1,
            body: [
              [
                { text: 'Pos.', style: ['tableItemsGrey'] },
                { text: 'Beschreibung' },
                { text: 'Menge', style: ['tableItemsGrey', 'tableItemsRight'] },
                { text: 'Preis', style: ['tableItemsGrey', 'tableItemsRight'] },
                { text: 'Summe', style: ['tableItemsRight'] },
              ],
              ...invoiceItemTableArr,
            ],
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === node.table.body.length ? 1 : 0;
            },
            vLineWidth: function (i, node) {
              return i === 0 || i === node.table.widths.length ? 1 : 0;
            },
            hLineColor: function (i, node) {
              return '#7f7f7f';
            },
            vLineColor: function (i, node) {
              return '#7f7f7f';
            },
            fillColor: function (i, node) {
              return i === 0 || i === node.table.body.length - 1 ? '#8c8c8c' : '';
            },
            paddingTop: (i, node) => {
              if (node.text && (node.text as string).includes('\n')) {
                return 0;
              } else {
                return 4;
              }
            },
          },
        },
      ],

      footer: {
        margin: [70, 0, 43, 57],
        stack: [
          {
            columns: [
              {
                style: { alignment: 'left' },
                stack: [
                  { text: 'Bankverbindung', bold: true },
                  {
                    table: {
                      widths: [50, '*'],
                      body: [
                        ['Name', 'Andreas Nickel'],
                        ['BIC', 'SOLADEST600'],
                        ['IBAN', 'DE34 6005 0101 7006 8411 01'],
                      ],
                    },
                    layout: 'noBorders',
                  },
                ],
              },
              {
                style: { alignment: 'right' },
                stack: [
                  { text: 'Kontakt', bold: true },
                  {
                    table: {
                      widths: ['*'],
                      body: [['TechnikFLG'], ['Fanny-Leicht-Straße 13'], ['70563 Stuttgart']],
                    },
                    layout: 'noBorders',
                  },
                ],
              },
            ],
          },
          {
            bold: true,
            style: { alignment: 'center' },
            marginTop: 10,
            columns: [
              { text: 'info@technikflg.com', alignment: 'right', marginRight: 10 },
              { text: 'www.technikflg.com', alignment: 'left', marginLeft: 10 },
            ],
          },
        ],
      },

      images: {
        logo: logoURL,
        //barcode: barcode,
      },

      defaultStyle: {
        font: 'Roboto',
        fontSize: 10,
      },

      styles: {
        addressSender: {
          font: 'Arial',
          fontSize: 9,
          //color: "#8c8c8c",
          color: '#595959',
          bold: true,
        },
        addressText: {
          font: 'Arial',
          fontSize: 11,
          lineHeight: 1.1,
        },
        header: {
          fontSize: 12,
          bold: true,
        },
        tableItems: {},
        tableItemsGrey: {
          color: '#3d3d3d',
        },
        tableItemsRight: {
          alignment: 'right',
        },
        preview: {
          font: 'Roboto',
          fontSize: 100,
          bold: true,
          color: '#3d3d3d',
          opacity: 0.6,
          alignment: 'center',
        },
      },
    };

    if (preview) {
      (docDefinition.content as any[]).push({
        text: 'Preview',
        style: 'preview',
        absolutePosition: { x: 70, y: 400 },
      });
    }

    return docDefinition;
  }
}
